import Dropdown from 'components/shared/app/Dropdown';
import BarGraphHorizontal from 'components/shared/graphs/BarGraphHorizontal';
import DoughnutGraph from 'components/shared/graphs/DoughnutGraph';
import { findSuggestedMax } from 'components/shared/graphs/utils';
import Config from 'lib/Config';
import { CampaignDataFilter } from 'lib/data/models/CampaignObjects';
import { Goal } from 'lib/data/models/Goals';
import { ProviderNames } from 'lib/data/models/Providers';
import { PageHelper } from 'lib/helpers/PageHelper.js';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import { getAccountProviders, getInnerError, renderLoader } from 'lib/utils/adaya/AdayaUtils';
import { arrayUntil } from 'lib/utils/ObjectUtils.js';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { getGoal } from 'utils/adaya/AdayaUtils';
import { combineAllDailyCampaignData } from 'utils/adaya/ProviderUtils';
import { formatCurrency } from 'utils/currency';
import { createIsoTimeRange, DAYS, daysInThisMonth, now, thisMonthSoFarLength } from 'utils/DateUtils';
import { getLogger } from 'utils/logging';
import { orZero } from 'utils/NumUtils';
import Menu from './Mobile/menu.jsx';
import StreakMessaging from './StreakMessaging';
import { renderData as renderDataBarHorizontal, renderOptions as renderOptionsBarHorizontal } from './utils/renderer-bar-horizonatal-chart';
import { renderData as renderDataDoughnut, renderOptions as renderOptionsDoughnut } from './utils/renderer-doughnut';

const { log } = getLogger('Track', { color: 'yellow', enabled: false });

const oneDate = now().subtract(1, 'days');
const monthName = oneDate.format('MMMM');
const daysInMonth = oneDate.daysInMonth();
const dropdownTabs = [
    { value: 'orders', label: 'Orders' },
    { value: 'revenue', label: 'Store Revenue' },
    { value: 'adspend', label: 'AdSpend' },
    { value: 'mer', label: 'MER' },
    { value: 'cpp', label: 'CPP' },
    { value: 'aov', label: 'AOV' }
];

const pageHelper = new PageHelper();

export default Track = (props) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [thisMonthData, setThisMonthData] = useState(undefined);
    const [yesterdayData, setYesterdayData] = useState(undefined);

    const [showError, setShowError] = useState(false);
    const [dataReady, setDataReady] = useState(false);
    const [drawBarGraph, setDrawBarGraph] = useState(false);
    const [tab, setTab] = useState('orders');
    const [doughnutOptions, setDoughnutOptions] = useState({});
    const [barOptions, setBarOptions] = useState({});

    const [dailyGoalMetrics, setDailyGoalMetrics] = useState({
        ordersGoal: 0,
        productsGoal: 0,
        revenueGoal: 0,
        adspendGoal: 0,
        merGoal: 0,
        roasGoal: 0,
        cppGoal: 0,
        aovGoal: 0
    });

    const [yesterdayMetrics, setYesterdayMetrics] = useState({
        orders: 0,
        revenue: 0,
        adspend: 0,
        mer: 0,
        roas: 0,
        cpp: 0,
        aov: 0
    });

    const [monthMaxMetrics, setMonthMaxMetrics] = useState({
        productsSuggestedMax: 0,
        revenueSuggestedMax: 0,
        adspendSuggestedMax: 0,
        merSuggestedMax: 0,
        roasSuggestedMax: 0,
        cppSuggestedMax: 0,
        aovSuggestedMax: 0
    });

    const [monthTotal, setMonthTotal] = useState({
        ordersAvg: 0,
        revenueAvg: 0,
        spendAvg: 0,
        mer: 0,
        roas: 0,
        cpp: 0,
        aov: 0
    });

    const user = props.auth.user;
    const account = user?.activeAccount;

    useEffect(async () => {
        initializeDates();
        await fetchAllData();
    }, [account]);

    initializeDates = () => {
        if (account) {
            const daysInMonth = daysInThisMonth();

            // todo: get goal from GoalHelper
            const revenueGoal = getGoal(account, Goal.Revenue);
            const adspendGoal = getGoal(account, Goal.Adspend);
            const ordersGoal = getGoal(account, Goal.Orders);
            const productsGoal = getGoal(account, Goal.Products);
            const merGoal = getGoal(account, Goal.Mer);
            const roasGoal = getGoal(account, Goal.Roas);
            const cppGoal = getGoal(account, Goal.Cpp);
            const aovGoal = getGoal(account, Goal.Aov);

            setDailyGoalMetrics({
                productsGoal: productsGoal / daysInMonth,
                ordersGoal: ordersGoal / daysInMonth,
                revenueGoal: revenueGoal / daysInMonth,
                adspendGoal: adspendGoal / daysInMonth,
                merGoal,
                roasGoal,
                cppGoal,
                aovGoal
            });
        }
    }

    // Calculate Daily Goals
    useEffect(() => {
    }, [props.auth.user]);

    // Calculate Yesterday and Month Max Metrics when data sets change
    useEffect(() => {
        if (yesterdayData && thisMonthData) {

            setYesterdayMetrics({
                orders: yesterdayData.totals.orders,
                revenue: yesterdayData.totals.revenue,
                adspend: yesterdayData.totals.adspend,
                mer: yesterdayData.totals.mer,
                roas: yesterdayData.totals.roas,
                cpp: yesterdayData.totals.cpp,
                aov: yesterdayData.totals.aov
            });

            const days = thisMonthData.length;
            const _thisMonthSoFarLength = thisMonthSoFarLength();
            const monthDataSoFar = arrayUntil(thisMonthData, _thisMonthSoFarLength);

            const monthProducts = days ? monthDataSoFar.map(d => d.totals.items) : [];
            const monthProductsAvg = monthProducts.length ? (monthProducts.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthProductsMax = monthProducts.length ? Math.max(...monthProducts) : 0;
            const monthProductsSuggestedMax = findSuggestedMax(monthProductsAvg, monthProductsMax, dailyGoalMetrics.productsGoal, .1);

            const monthOrders = days ? monthDataSoFar.map(d => d.totals.orders) : [];
            const monthOrdersAvg = monthOrders.length ? (monthOrders.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;

            const monthRevenues = days ? monthDataSoFar.map(d => d.totals.revenue) : [];
            const monthRevenueAvg = monthRevenues.length ? (monthRevenues.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthRevenueMax = monthRevenues.length ? Math.max(...monthRevenues) : 0;
            const monthRevenueSuggestedMax = monthRevenues.length ? findSuggestedMax(monthRevenueAvg, monthRevenueMax, dailyGoalMetrics.revenueGoal, .1) : 0;

            const monthAdspends = days ? monthDataSoFar.map(d => d.totals.adspend) : [];
            const monthAdspendAvg = monthAdspends.length ? (monthAdspends.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthAdspendMax = monthAdspends.length ? Math.max(...monthAdspends) : 0;
            const monthAdspendSuggestedMax = monthAdspends.length ? findSuggestedMax(monthAdspendAvg, monthAdspendMax, dailyGoalMetrics.adspendGoal, .2) : 0;

            const monthCpps = days ? monthDataSoFar.map(d => d.totals.cpp) : [];
            const monthCppAvg = monthCpps.length ? (monthCpps.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthCppMax = monthCpps.length ? Math.max(...monthCpps) : 0;
            const monthCppSuggestedMax = monthCpps.length ? findSuggestedMax(monthCppAvg, monthCppMax, dailyGoalMetrics.cppGoal, .1) : 0;

            const monthMers = days ? monthDataSoFar.map(d => d.totals.mer) : [];
            const monthMerAvg = monthMers.length ? (monthMers.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthMerMax = monthMers.length ? Math.max(...monthMers) : 0;
            const monthMerSuggestedMax = monthMers.length ? findSuggestedMax(monthMerAvg, monthMerMax, dailyGoalMetrics.merGoal, .2) : 0;

            const monthRoases = days ? monthDataSoFar.map(d => d.totals.roas) : [];
            const monthRoasAvg = monthRoases.length ? (monthRoases.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthRoasMax = monthRoases.length ? Math.max(...monthRoases) : 0;
            const monthRoasSuggestedMax = monthRoases.length ? findSuggestedMax(monthRoasAvg, monthRoasMax, dailyGoalMetrics.roasGoal, .2) : 0;

            const monthAovs = days ? monthDataSoFar.map(d => d.totals.aov) : [];
            const monthAovAvg = monthAovs.length ? (monthAovs.reduce((a, b) => a + b) / _thisMonthSoFarLength) : 0;
            const monthAovMax = monthRoases.length ? Math.max(...monthAovs) : 0;
            const monthAovSuggestedMax = monthRoases.length ? findSuggestedMax(monthAovAvg, monthAovMax, dailyGoalMetrics.aovGoal, .1) : 0;

            setMonthMaxMetrics({
                productsSuggestedMax: monthProductsSuggestedMax,
                revenueSuggestedMax: monthRevenueSuggestedMax,
                adspendSuggestedMax: monthAdspendSuggestedMax,
                merSuggestedMax: monthMerSuggestedMax,
                roasSuggestedMax: monthRoasSuggestedMax,
                cppSuggestedMax: monthCppSuggestedMax,
                aovSuggestedMax: monthAovSuggestedMax
            });

            setMonthTotal({
                ordersAvg: monthOrdersAvg,
                revenueAvg: monthRevenueAvg,
                spendAvg: monthAdspendAvg,
                mer: monthMerAvg,
                roas: monthRoasAvg,
                cpp: monthCppAvg,
                aov: monthAovAvg
            });
        }
    }, [yesterdayData, thisMonthData]);

    // fetch for all date ranges in parallel
    const fetchAllData = async () => {
        log(`fetchAllData`, account);
        if (!account) return;
        setLoading(true);

        try {
            await Promise.all([
                fetchData('yesterday'),
                fetchData('thisMonth')
            ]);
        } catch (e) {
            log(`Exception fetching data`, e)
            setError(getInnerError(e));
        }

        setLoading(false);
    };

    // fetch for single date range
    const fetchData = async (range) => {
        log(`fetchData`, range);
        try {
            const filter = getFilterFromSelection(range);
            // get both in parallel
            let r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, range);
        } catch (e) {
            throw e;
        }
    };

    const onDataResponse = (r: ManagedRequest, range) => {
        try {
            if (!r) throw "Response was empty.";
            log('onDataResponse', r, range);

            // Todo: could be handled better probably.

            if (r.status == RequestStatus.FINISHED) {
                if (range == 'yesterday') {
                    setYesterdayData(combineAllDailyCampaignData(r.response));
                } else if (range == 'thisMonth') {
                    const mappedData = Object.values(r.response).map(daily => ({
                        day: daily.dayStart,
                        totals: daily.totals
                    }));
                    setThisMonthData(mappedData);
                }
            } else {
                // shouldn't get here, it's handled through exceptions  mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    const updateTab = (tabId) => {
        setTab(tabId);
    }

    // Track needs all campaign data except Adaya
    const getFilterFromSelection = (range): CampaignDataFilter => {
        let timeRange = createIsoTimeRange(range);
        if (!timeRange)`A timeRange could not be created from the current selection: ${range}`;
        if (!account) throw `No account found for the current user.`;
        return {
            account,
            timeRange,
            providers: getAccountProviders(account, [ProviderNames.ADAYA]).map(p => p.name)
        };
    }

    const positiveOrNegative = useCallback((tab) => {
        switch (tab) {
            case 'orders':
                return (yesterdayMetrics.orders >= dailyGoalMetrics.ordersGoal) ? 'positive' : 'negative';
            case 'revenue':
                return (yesterdayMetrics.revenue >= dailyGoalMetrics.revenueGoal) ? 'positive' : 'negative';
            case 'adspend':
                return (yesterdayMetrics.adspend >= dailyGoalMetrics.adspendGoal) ? 'positive' : 'negative';
            case 'mer':
                return (yesterdayMetrics.mer >= dailyGoalMetrics.merGoal) ? 'positive' : 'negative';
            case 'roas':
                return (yesterdayMetrics.roas >= dailyGoalMetrics.roasGoal) ? 'positive' : 'negative';
            case 'cpp':
                return (yesterdayMetrics.cpp < dailyGoalMetrics.cppGoal) ? 'positive' : 'negative';
            case 'aov':
                return (yesterdayMetrics.aov >= dailyGoalMetrics.aovGoal) ? 'positive' : 'negative';
            default:
                return '';
        }
    }, [yesterdayMetrics, dailyGoalMetrics]);

    const renderYesterdayValue = useCallback((tab) => {
        // @TODO: enum the possible tab values, which are: orders, revenue, adspend, roas, cpp, aov
        switch (tab) {
            case 'orders':
                return parseFloat(yesterdayMetrics.orders).toFixed(0).toLocaleString("en-US");
            case 'revenue':
                return formatCurrency(parseFloat(yesterdayMetrics.revenue));
            case 'adspend':
                return formatCurrency(parseFloat(yesterdayMetrics.adspend));
            case 'mer':
                return parseFloat(yesterdayMetrics.mer).toFixed(2).toLocaleString("en-US");
            case 'roas':
                return parseFloat(yesterdayMetrics.roas).toFixed(2).toLocaleString("en-US");
            case 'cpp':
                return formatCurrency(parseFloat(yesterdayMetrics.cpp));
            case 'aov':
                return formatCurrency(parseFloat(yesterdayMetrics.aov));
            default:
                return 'N/A';
        }
    }, [yesterdayMetrics]);

    const renderGoalValue = useCallback((tab) => {
        // @TODO: enum the possible tab values, which are: orders, revenue, adspend, roas, cpp, aov
        switch (tab) {
            case 'orders':
                return parseFloat(dailyGoalMetrics.ordersGoal).toFixed(0).toLocaleString("en-US");
            case 'revenue':
                return formatCurrency(parseFloat(dailyGoalMetrics.revenueGoal));
            case 'adspend':
                return formatCurrency(parseFloat(dailyGoalMetrics.adspendGoal));
            case 'mer':
                return parseFloat(dailyGoalMetrics.merGoal).toFixed(2).toLocaleString("en-US");
            case 'roas':
                return parseFloat(dailyGoalMetrics.roasGoal).toFixed(2).toLocaleString("en-US");
            case 'cpp':
                return formatCurrency(parseFloat(dailyGoalMetrics.cppGoal));
            case 'aov':
                return formatCurrency(parseFloat(dailyGoalMetrics.aovGoal));
            default:
                return 'N/A';
        }
    }, [dailyGoalMetrics]);

    useEffect(() => {
        setDataReady(false);
        setDrawBarGraph(false);
    }, [tab]);

    useEffect(() => {
        if (thisMonthData) {
            let _doughnutOptions, _barOptions, data, average = 0, valueFunction, valueFormatFunction;

            switch (tab) {
                case 'orders':
                    _doughnutOptions = {
                        title: 'Orders',
                        data: renderDataDoughnut(yesterdayMetrics.orders, dailyGoalMetrics.ordersGoal),
                        target: dailyGoalMetrics.ordersGoal,
                        yesterday: yesterdayMetrics.orders,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => x.totals.orders;
                    valueFormatFunction = x => { return parseFloat(x.toFixed(0)).toLocaleString("en-US") };

                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.ordersGoal;
                    data.dailyAverage = monthTotal.ordersAvg;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.ordersGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.productsSuggestedMax, props.isMobile, false)
                    };
                    break;

                case 'revenue':
                    _doughnutOptions = {
                        title: 'Store Revenue',
                        data: renderDataDoughnut(yesterdayMetrics.revenue, dailyGoalMetrics.revenueGoal),
                        target: dailyGoalMetrics.revenueGoal,
                        yesterday: yesterdayMetrics.revenue,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => x.totals.revenue;
                    valueFormatFunction = x => formatCurrency(x);
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.revenueGoal;
                    data.dailyAverage = monthTotal.revenueAvg;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.revenueGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.revenueSuggestedMax, props.isMobile, false)
                    }
                    break;

                case 'adspend':
                    _doughnutOptions = {
                        title: 'Adspend',
                        data: renderDataDoughnut(yesterdayMetrics.adspend, dailyGoalMetrics.adspendGoal),
                        target: dailyGoalMetrics.adspendGoal,
                        yesterday: yesterdayMetrics.adspend,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => x.totals.adspend;
                    valueFormatFunction = x => formatCurrency(x);
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.adspendGoal;
                    data.dailyAverage = monthTotal.spendAvg;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.adspendGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.adspendSuggestedMax, props.isMobile, false),
                    }
                    break;

                case 'mer':
                    _doughnutOptions = {
                        title: 'MER',
                        data: renderDataDoughnut(yesterdayMetrics.mer, dailyGoalMetrics.merGoal),
                        target: dailyGoalMetrics.merGoal,
                        yesterday: yesterdayMetrics.mer,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => orZero(x.totals.mer);
                    valueFormatFunction = x => { return parseFloat(x.toFixed(2)).toLocaleString("en-US") };
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.merGoal;
                    data.dailyAverage = monthTotal.mer;
                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.merGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.merSuggestedMax, props.isMobile, false)
                    }
                    break;

                case 'roas':
                    _doughnutOptions = {
                        title: 'MER',
                        data: renderDataDoughnut(yesterdayMetrics.roas, dailyGoalMetrics.roasGoal),
                        target: dailyGoalMetrics.roasGoal,
                        yesterday: yesterdayMetrics.roas,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => orZero(x.totals.roas);
                    valueFormatFunction = x => { return parseFloat(x.toFixed(2)).toLocaleString("en-US") };
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.roasGoal;
                    data.dailyAverage = monthTotal.roas;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.roasGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.roasSuggestedMax, props.isMobile, false)
                    }
                    break;

                case 'cpp':
                    _doughnutOptions = {
                        title: 'Cost Per Purchase',
                        data: renderDataDoughnut(yesterdayMetrics.cpp, dailyGoalMetrics.cppGoal),
                        target: dailyGoalMetrics.cppGoal,
                        yesterday: yesterdayMetrics.cpp,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => orZero(x.totals.cpp);
                    valueFormatFunction = x => formatCurrency(x);
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.cppGoal;
                    data.dailyAverage = monthTotal.cpp;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.cppGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.cppSuggestedMax, props.isMobile, true),
                    }
                    break;

                case 'aov':
                    _doughnutOptions = {
                        title: 'Average Order Value',
                        data: renderDataDoughnut(yesterdayMetrics.aov, dailyGoalMetrics.aovGoal),
                        target: dailyGoalMetrics.aovGoal,
                        yesterday: yesterdayMetrics.aov,
                        options: renderOptionsDoughnut()
                    };

                    valueFunction = x => orZero(x.totals.aov);
                    valueFormatFunction = x => formatCurrency(x);
                    data = renderDataBarHorizontal(thisMonthData, DAYS, valueFunction);
                    data.dailyGoal = dailyGoalMetrics.aovGoal;
                    data.dailyAverage = monthTotal.aov;

                    _barOptions = {
                        data,
                        target: average,
                        goal: dailyGoalMetrics.aovGoal,
                        options: renderOptionsBarHorizontal(valueFormatFunction, monthMaxMetrics.aovSuggestedMax, props.isMobile, false)
                    }
                    break;
            }

            _barOptions.options.title = monthName.toUpperCase();

            setDoughnutOptions(_doughnutOptions);
            setBarOptions(_barOptions);
            setTimeout(() => {
                setDataReady(true);
            }, 0);
        }
    }, [tab, thisMonthData, yesterdayMetrics, dailyGoalMetrics]);

    useEffect(() => {
        if (dataReady) {
            setDrawBarGraph(true);
        }
    }, [dataReady]);

    return (
        <React.Fragment>
            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Track</title></Helmet>

            {!props.isMobile &&

                <div className={'track-page page-layout ' + (error != undefined ? ' has-error' : '')}>

                    {loading && renderLoader()}

                    {!showError && dataReady &&

                        <div className="cols flex-no-wrap">

                            <div className="col tabs">
                                <div className="tab messaging positive">
                                    <StreakMessaging tab={tab} />
                                </div>
                                <div className={'tab orders ' + (tab == 'orders' ? 'active' : '')} onClick={() => updateTab('orders')}>
                                    <div className={'num ' + positiveOrNegative('orders')}>{parseFloat(yesterdayMetrics.orders).toFixed(0).toLocaleString("en-US")}</div>
                                    <div className="label">Orders</div>
                                </div>
                                <div className={'tab revenue ' + (tab == 'revenue' ? 'active' : '')} onClick={() => updateTab('revenue')}>
                                    <div className={'num ' + positiveOrNegative('revenue')}>{formatCurrency(parseFloat(yesterdayMetrics.revenue))}</div>
                                    <div className="label">Store Revenue</div>
                                </div>
                                <div className={'tab adspend ' + (tab == 'adspend' ? 'active' : '')} onClick={() => updateTab('adspend')}>
                                    <div className={'num ' + positiveOrNegative('adspend')}>{formatCurrency(parseFloat(yesterdayMetrics.adspend))}</div>
                                    <div className="label">Adspend</div>
                                </div>
                                {/* <div className={'tab roas ' + (tab == 'roas' ? 'active' : '')} onClick={() => updateTab('roas')}>
                                    <div className={'num ' + positiveOrNegative('roas')}>{parseFloat(yesterdayMetrics.roas).toFixed(2).toLocaleString("en-US")}</div>
                                    <div className="label">MER</div>
                                </div> */}
                                <div className={'tab mer ' + (tab == 'mer' ? 'active' : '')} onClick={() => updateTab('mer')}>
                                    <div className={'num ' + positiveOrNegative('mer')}>{parseFloat(yesterdayMetrics.mer).toFixed(2).toLocaleString("en-US")}</div>
                                    <div className="label">MER</div>
                                </div>
                                <div className={'tab cpp ' + (tab == 'cpp' ? 'active' : '')} onClick={() => updateTab('cpp')}>
                                    <div className={'num ' + positiveOrNegative('cpp')}>{formatCurrency(parseFloat(yesterdayMetrics.cpp))}</div>
                                    <div className="label">Cost Per Purchase</div>
                                </div>
                                <div className={'tab aov ' + (tab == 'aov' ? 'active' : '')} onClick={() => updateTab('aov')}>
                                    <div className={'num ' + positiveOrNegative('aov')}>{formatCurrency(parseFloat(yesterdayMetrics.aov))}</div>
                                    <div className="label">Average Order Value</div>
                                </div>
                            </div>

                            <div className="col section">
                                <div className="chart">
                                    <div className="title">{doughnutOptions.title}</div>
                                    {dataReady && <DoughnutGraph
                                        title={''}
                                        data={doughnutOptions.data}
                                        options={doughnutOptions.options}
                                        target={doughnutOptions.target}
                                        yesterday={doughnutOptions.yesterday}
                                    />}
                                </div>

                                <div className="cols">
                                    <div className="col green">
                                        <div className="label">Yesterday</div>
                                        <div className="num">{renderYesterdayValue(tab)}</div>
                                    </div>
                                    <div className="col blue">
                                        <div className="label">Goal</div>
                                        <div className="num">{renderGoalValue(tab)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col right">
                                <div className="inner">
                                    <div className="label"></div>
                                    <div className="chart">
                                        {dataReady && drawBarGraph && <BarGraphHorizontal
                                            header={monthName}
                                            name={barOptions.title}
                                            data={barOptions.data}
                                            options={barOptions.options}
                                            target={barOptions.target}
                                            dailyTarget={barOptions.goal}
                                            monthLength={daysInMonth}
                                            dataReady={dataReady}
                                        />}
                                    </div>
                                </div>
                            </div>

                        </div>

                    }

                    {showError && <div className="cols flex-no-wrap">
                        Sorry, an error occured. Please try to load the page again.
                    </div>
                    }
                </div>
            }

            {props.isMobile && dataReady &&
                <div className="track-mobile page-layout">

                    {loading && renderLoader()}

                    <div className={'cols flex-no-wrap '}>
                        <div className="col tabs">
                            <div className="tab messaging positive">
                                <StreakMessaging tab={tab} />
                            </div>
                        </div>

                        <div className="swiper-slide">
                            <div className="col section">
                                <div className="chart">

                                    {dataReady &&
                                        <div className="metrics">
                                            <Dropdown
                                                selectedItem={tab}
                                                //selectedIndex={dropdownTabs.findIndex(t => t.value == tab)}
                                                setSelection={updateTab}
                                                items={dropdownTabs}
                                                setFixedWidth={true}
                                            />
                                        </div>
                                    }

                                    {dataReady && <DoughnutGraph
                                        title={doughnutOptions.title}
                                        data={doughnutOptions.data}
                                        options={doughnutOptions.options}
                                        target={doughnutOptions.target}
                                        yesterday={doughnutOptions.yesterday}
                                        mobile={true}
                                    />}

                                </div>

                                <div className="cols">
                                    <div className="col green">
                                        <div className="label">Yesterday</div>
                                        <div className="num">{renderYesterdayValue(tab)}</div>
                                    </div>
                                    <div className="col blue">
                                        <div className="label">Goal</div>
                                        <div className="num">{renderGoalValue(tab)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col right">
                                <div className="inner">
                                    <div className="label"></div>
                                    <div className="chart">
                                        {dataReady && <Menu header={doughnutOptions.title} className="metrics" mobile={true}></Menu>}
                                        {dataReady && <BarGraphHorizontal
                                            header={monthName}
                                            name={barOptions.title}
                                            data={barOptions.data}
                                            options={barOptions.options}
                                            target={barOptions.target}
                                            dailyTarget={barOptions.goal}
                                            monthLength={daysInMonth}
                                            dataReady={dataReady}
                                            mobile={true}
                                        />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }

        </React.Fragment>
    )
}